import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  Monitor, AlertTriangle, Shield, Activity, RefreshCw, Loader2,
  Terminal, Network, FileText, Database, Search, Server, Wifi, WifiOff,
  Copy, Download, Heart, Cpu, HardDrive, Clock, Link2, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const SEVERITY_COLORS = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-gray-100 text-gray-600 border-gray-200',
};

const EVENT_ICONS = {
  process_create: Terminal,
  network_connect: Network,
  file_create: FileText,
  registry_modify: Database,
  dns_query: Search,
};

export default function SysmonDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Endpoint Telemetry');
  const [dashboard, setDashboard] = useState(null);
  const [view, setView] = useState('overview');
  const [events, setEvents] = useState([]);
  const [iocSummary, setIocSummary] = useState(null);
  const [filterHost, setFilterHost] = useState('');
  const [filterType, setFilterType] = useState('');
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState(null);
  const [correlations, setCorrelations] = useState(null);
  const [installScript, setInstallScript] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [deployment, setDeployment] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [liveEvents, setLiveEvents] = useState([]);
  const [liveConnected, setLiveConnected] = useState(false);
  const [liveFilters, setLiveFilters] = useState({ severity: '', hostname: '', flagged_only: false });
  const wsRef = React.useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get('/api/sysmon/dashboard');
      setDashboard(data);
      if (data.total_events === 0) {
        await api.post('/api/sysmon/seed-demo');
        const { data: d2 } = await api.get('/api/sysmon/dashboard');
        setDashboard(d2);
      }
    } catch (e) { /* handled */ } finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadView = async (v) => {
    setView(v);
    if (v === 'events') {
      let q = '/api/sysmon/events?limit=100';
      if (filterHost) q += `&hostname=${filterHost}`;
      if (filterType) q += `&event_type=${filterType}`;
      const { data } = await api.get(q);
      setEvents(data.events || []);
    } else if (v === 'ioc') {
      const { data } = await api.get('/api/sysmon/ioc-summary');
      setIocSummary(data);
    } else if (v === 'flagged') {
      const { data } = await api.get('/api/sysmon/events?flagged=true&limit=50');
      setEvents(data.events || []);
    } else if (v === 'agents') {
      const { data } = await api.get('/api/sysmon/agents');
      setAgents(data);
    } else if (v === 'correlate') {
      const { data } = await api.get('/api/sysmon/correlate?hours=48');
      setCorrelations(data);
    } else if (v === 'deployment') {
      const { data } = await api.get('/api/sysmon/deployment/status');
      setDeployment(data);
    }
  };

  const loadBriefing = async () => {
    setBriefingLoading(true);
    try {
      const { data } = await api.get('/api/sysmon/threat-briefing?hours=24');
      setBriefing(data);
    } catch { toast.error('Briefing generation failed'); }
    finally { setBriefingLoading(false); }
  };

  const connectLiveStream = async () => {
    let token = '';
    try {
      const { data } = await api.get('/api/auth/ws-ticket');
      token = data?.token || '';
    } catch {
      toast.error('Unable to authorize live stream');
      return;
    }
    const wsBase = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsBase}/api/ws/sysmon?token=${token}`);
    wsRef.current = ws;
    ws.onopen = () => { setLiveConnected(true); toast.success('Live stream connected'); };
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === 'sysmon_event') {
        setLiveEvents(prev => [data.event, ...prev].slice(0, 200));
      } else if (data.type === 'sysmon_alert') {
        setLiveEvents(prev => [{ ...data.alert, flagged: true, _isAlert: true }, ...prev].slice(0, 200));
        toast.error(`IOC Alert: ${data.alert.hostname} — ${data.alert.reasons?.[0] || 'Suspicious'}`, { duration: 5000 });
      }
    };
    ws.onclose = () => { setLiveConnected(false); };
    ws.onerror = () => { setLiveConnected(false); };
  };

  const disconnectLiveStream = () => {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    setLiveConnected(false);
  };

  React.useEffect(() => { return () => { if (wsRef.current) wsRef.current.close(); }; }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInstallScript = async (os) => {
    const { data } = await api.get(`/api/sysmon/agents/install-script/${os}`);
    setInstallScript(data);
  };

  const copyScript = () => {
    if (installScript?.script) { navigator.clipboard.writeText(installScript.script); toast.success('Script copied!'); }
  };

  const viewAgent = async (agentId) => {
    const { data } = await api.get(`/api/sysmon/agents/${agentId}`);
    setSelectedAgent(data);
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="sysmon-dashboard">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
                <Monitor className="w-6 h-6 text-[#D4A843]" /> Endpoint Telemetry
              </h1>
              <p className="text-sm text-gray-500 mt-1">Sysmon agent events, IOC detection, and process trees</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          </div>

          {/* Sub-nav */}
          <div className="flex gap-1 border-b border-gray-200 pb-0">
            {[{k:'overview',l:'Overview'},{k:'live',l:'Live Stream'},{k:'agents',l:'Agents'},{k:'deployment',l:'Deployment'},{k:'events',l:'Events'},{k:'flagged',l:'IOCs'},{k:'correlate',l:'Correlation'},{k:'briefing',l:'Threat Brief'},{k:'deploy',l:'Install'}].map(t => (
              <button key={t.k} onClick={() => loadView(t.k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${view === t.k ? 'border-[#D4A843] text-[#D4A843]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid={`sysmon-tab-${t.k}`}>{t.l}</button>
            ))}
          </div>

          {/* Overview */}
          {view === 'overview' && dashboard && (
            <div className="space-y-4" data-testid="sysmon-overview">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
                  <Activity className="w-5 h-5 text-[#D4A843] mb-2" />
                  <p className="font-heading text-2xl font-bold text-gray-900">{dashboard.total_events}</p>
                  <p className="text-xs text-gray-500">Total Events</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
                  <Activity className="w-5 h-5 text-emerald-600 mb-2" />
                  <p className="font-heading text-2xl font-bold text-emerald-700">{dashboard.events_24h}</p>
                  <p className="text-xs text-emerald-600">Last 24h</p>
                </div>
                <div className={`rounded-lg p-5 hover-lift ${dashboard.flagged_24h > 0 ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'}`}>
                  <AlertTriangle className={`w-5 h-5 mb-2 ${dashboard.flagged_24h > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                  <p className={`font-heading text-2xl font-bold ${dashboard.flagged_24h > 0 ? 'text-red-700' : 'text-gray-900'}`}>{dashboard.flagged_24h}</p>
                  <p className="text-xs text-gray-500">IOCs (24h)</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
                  <Monitor className="w-5 h-5 text-[#D4A843] mb-2" />
                  <p className="font-heading text-2xl font-bold text-[#D4A843]">{dashboard.host_count}</p>
                  <p className="text-xs text-[#D4A843]">Endpoints</p>
                </div>
              </div>

              {/* Event Type Breakdown */}
              {Object.keys(dashboard.event_type_counts || {}).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Event Types (24h)</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {Object.entries(dashboard.event_type_counts).map(([type, count]) => {
                      const Icon = EVENT_ICONS[type] || Activity;
                      return (
                        <div key={type} className="bg-gray-50 rounded-lg p-3 text-center cursor-pointer hover:bg-gray-100" onClick={() => { setFilterType(type); loadView('events'); }}>
                          <Icon className="w-4 h-4 text-[#D4A843] mx-auto mb-1" />
                          <p className="text-lg font-bold text-gray-900">{count}</p>
                          <p className="text-[10px] text-gray-500">{type.replace(/_/g, ' ')}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Hosts */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Monitored Endpoints</h3>
                <div className="flex flex-wrap gap-2">
                  {(dashboard.hosts || []).map(h => (
                    <button key={h} onClick={() => { setFilterHost(h); loadView('events'); }} className="px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs font-mono font-medium text-gray-700 hover:bg-[#D4A843]/5 hover:border-[#D4A843]/20 transition-colors" data-testid={`sysmon-host-${h}`}>{h}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Live Stream */}
          {view === 'live' && (
            <div className="space-y-4" data-testid="sysmon-live">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#D4A843]" /> Real-Time Event Stream
                  {liveConnected && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
                </h2>
                <div className="flex items-center gap-2">
                  <select value={liveFilters.severity} onChange={e => setLiveFilters({...liveFilters, severity: e.target.value})} className="h-8 rounded-lg border border-gray-200 px-2 text-[10px] bg-white" data-testid="live-filter-severity">
                    <option value="">All Severity</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                  <label className="flex items-center gap-1 text-[10px] text-gray-600"><input type="checkbox" checked={liveFilters.flagged_only} onChange={e => setLiveFilters({...liveFilters, flagged_only: e.target.checked})} className="rounded" /> IOCs only</label>
                  {!liveConnected ? (
                    <Button size="sm" onClick={connectLiveStream} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" data-testid="live-connect"><Wifi className="w-3 h-3 mr-1" /> Connect</Button>
                  ) : (
                    <Button size="sm" onClick={disconnectLiveStream} variant="outline" className="text-red-500 rounded-lg text-xs" data-testid="live-disconnect"><WifiOff className="w-3 h-3 mr-1" /> Disconnect</Button>
                  )}
                </div>
              </div>

              {!liveConnected && liveEvents.length === 0 && (
                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                  <Activity className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-bold text-gray-900 mb-1">Real-Time Event Stream</p>
                  <p className="text-xs text-gray-400 mb-4">Connect to see Sysmon events as they arrive from your agents</p>
                  <Button onClick={connectLiveStream} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="live-connect-main"><Wifi className="w-3.5 h-3.5 mr-1" /> Start Live Stream</Button>
                </div>
              )}

              {(liveConnected || liveEvents.length > 0) && (
                <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                  <p className="text-[10px] text-gray-400 mb-1">{liveEvents.length} events buffered {liveConnected && '(streaming...)'}</p>
                  {liveEvents.filter(e => {
                    if (liveFilters.severity && e.severity !== liveFilters.severity) return false;
                    if (liveFilters.flagged_only && !e.flagged) return false;
                    return true;
                  }).map((e, i) => {
                    const Icon = EVENT_ICONS[e.event_type] || Activity;
                    return (
                      <div key={e.id || i} className={`p-3 rounded-lg border text-xs ${e.flagged ? 'bg-red-50/50 border-red-200' : 'bg-white border-gray-200'} ${i === 0 ? 'animate-fade-in' : ''}`} data-testid={`live-event-${i}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-3.5 h-3.5 ${e.flagged ? 'text-red-500' : 'text-gray-400'}`} />
                            <span className="font-mono font-bold text-gray-900">{e.hostname}</span>
                            <span className="text-gray-400">{e.event_type?.replace(/_/g, ' ')}</span>
                            {e.process_name && <span className="font-mono text-gray-600">{e.process_name}</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            {e._isAlert && <span className="text-[10px] bg-red-600 text-white px-1.5 py-0.5 rounded font-bold animate-pulse">ALERT</span>}
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${SEVERITY_COLORS[e.severity] || SEVERITY_COLORS.low}`}>{e.severity}</span>
                            <span className="text-[10px] text-gray-400">{e.timestamp ? new Date(e.timestamp).toLocaleTimeString() : 'now'}</span>
                          </div>
                        </div>
                        {e.ioc_reasons && <div className="mt-1 flex flex-wrap gap-1">{e.ioc_reasons.map((r, j) => <span key={`j-${j}`} className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">{r}</span>)}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Events List */}
          {(view === 'events' || view === 'flagged') && (
            <div className="space-y-2" data-testid="sysmon-events-list">
              {events.length === 0 && <p className="text-center text-gray-400 py-8 bg-white rounded-lg border border-gray-200">No events found</p>}
              {events.slice(0, 60).map(e => {
                const Icon = EVENT_ICONS[e.event_type] || Activity;
                return (
                  <div key={e.id} className={`bg-white border rounded-lg p-4 ${e.flagged ? 'border-red-200 bg-red-50/30' : 'border-gray-200'}`} data-testid={`sysmon-event-${e.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${e.flagged ? 'text-red-500' : 'text-gray-400'}`} />
                        <span className="text-xs font-mono font-bold text-gray-900">{e.hostname}</span>
                        <span className="text-[10px] text-gray-400">{e.event_type?.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${SEVERITY_COLORS[e.severity] || SEVERITY_COLORS.low}`}>{e.severity}</span>
                        <span className="text-[10px] text-gray-400">{new Date(e.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                    {e.process_name && <p className="text-xs text-gray-600"><span className="text-gray-400">Process:</span> <span className="font-mono">{e.process_name}</span> {e.parent_process ? `← ${e.parent_process}` : ''}</p>}
                    {e.command_line && <p className="text-[10px] text-gray-500 font-mono mt-1 truncate">{e.command_line}</p>}
                    {e.dest_ip && <p className="text-xs text-gray-600"><span className="text-gray-400">→</span> <span className="font-mono">{e.dest_ip}:{e.dest_port}</span></p>}
                    {e.dns_query && <p className="text-xs text-gray-600"><span className="text-gray-400">DNS:</span> <span className="font-mono">{e.dns_query}</span></p>}
                    {e.ioc_reasons && <div className="mt-2 flex flex-wrap gap-1">{e.ioc_reasons.map((r, i) => <span key={`item-${i}`} className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">{r}</span>)}</div>}
                  </div>
                );
              })}
            </div>
          )}

          {/* IOC Summary */}
          {view === 'ioc' && iocSummary && (
            <div className="space-y-4" data-testid="sysmon-ioc-summary">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-5"><p className="text-2xl font-bold text-red-700">{iocSummary.by_severity?.critical || 0}</p><p className="text-xs text-red-600">Critical</p></div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-5"><p className="text-2xl font-bold text-orange-700">{iocSummary.by_severity?.high || 0}</p><p className="text-xs text-orange-600">High</p></div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-5"><p className="text-2xl font-bold text-amber-700">{iocSummary.by_severity?.medium || 0}</p><p className="text-xs text-amber-600">Medium</p></div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5"><p className="text-2xl font-bold text-gray-700">{iocSummary.total_iocs}</p><p className="text-xs text-gray-500">Total IOCs</p></div>
              </div>
              {Object.keys(iocSummary.by_host || {}).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">IOCs by Host</h3>
                  <div className="space-y-2">{Object.entries(iocSummary.by_host).map(([host, count]) => (
                    <div key={host} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-xs font-mono font-bold text-gray-900">{host}</span>
                      <span className="text-xs font-bold text-red-600">{count} IOCs</span>
                    </div>
                  ))}</div>
                </div>
              )}
              {(iocSummary.recent_iocs || []).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Recent IOC Alerts</h3>
                  <div className="space-y-2">{iocSummary.recent_iocs.map(ioc => (
                    <div key={ioc.id} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono font-bold text-gray-900">{ioc.hostname} — {ioc.process_name}</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${SEVERITY_COLORS[ioc.severity]}`}>{ioc.severity}</span>
                      </div>
                      {ioc.ioc_reasons && <div className="mt-1 flex flex-wrap gap-1">{ioc.ioc_reasons.map((r, i) => <span key={`item-${i}`} className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">{r}</span>)}</div>}
                    </div>
                  ))}</div>
                </div>
              )}
            </div>
          )}

          {/* Agents */}
          {view === 'agents' && agents && (
            <div className="space-y-4" data-testid="sysmon-agents">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center hover-lift"><Server className="w-5 h-5 text-[#D4A843] mx-auto mb-1" /><p className="text-2xl font-bold text-gray-900">{agents.total}</p><p className="text-[10px] text-gray-500">Total Agents</p></div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 text-center hover-lift"><Wifi className="w-5 h-5 text-emerald-600 mx-auto mb-1" /><p className="text-2xl font-bold text-emerald-700">{agents.active}</p><p className="text-[10px] text-emerald-600">Active</p></div>
                <div className={`rounded-lg p-5 text-center hover-lift ${agents.offline > 0 ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'}`}><WifiOff className={`w-5 h-5 mx-auto mb-1 ${agents.offline > 0 ? 'text-red-600' : 'text-gray-400'}`} /><p className={`text-2xl font-bold ${agents.offline > 0 ? 'text-red-700' : 'text-gray-900'}`}>{agents.offline}</p><p className="text-[10px] text-gray-500">Offline</p></div>
              </div>
              <div className="space-y-3">
                {agents.agents.map(a => (
                  <div key={a.agent_id} className="bg-white border border-gray-200 rounded-lg p-4 hover-lift cursor-pointer" onClick={() => viewAgent(a.agent_id)} data-testid={`agent-${a.agent_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${a.health === 'healthy' ? 'bg-emerald-500' : a.health === 'degraded' ? 'bg-amber-500' : 'bg-red-500'}`} />
                        <div>
                          <p className="text-sm font-bold text-gray-900 font-mono">{a.hostname}</p>
                          <p className="text-[10px] text-gray-400">{a.os_type} | {a.ip_address} | v{a.version}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-[10px] text-gray-500">
                        {a.cpu_usage != null && <span className="flex items-center gap-1"><Cpu className="w-3 h-3" />{Math.round(a.cpu_usage)}%</span>}
                        {a.memory_usage != null && <span className="flex items-center gap-1"><HardDrive className="w-3 h-3" />{Math.round(a.memory_usage)}%</span>}
                        <span className="flex items-center gap-1"><Activity className="w-3 h-3" />{a.event_count} events</span>
                        {a.flagged_count > 0 && <span className="text-red-600 font-bold">{a.flagged_count} IOCs</span>}
                        <span className={`px-2 py-0.5 rounded font-bold uppercase ${a.health === 'healthy' ? 'bg-emerald-100 text-emerald-700' : a.health === 'degraded' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{a.health}</span>
                      </div>
                    </div>
                    {selectedAgent?.agent?.agent_id === a.agent_id && (
                      <div className="mt-4 pt-4 border-t border-gray-100 animate-fade-in" data-testid="agent-detail">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          <div className="text-center p-2 bg-gray-50 rounded"><p className="text-xs font-bold text-gray-900">{selectedAgent.recent_events?.length || 0}</p><p className="text-[10px] text-gray-500">Recent Events</p></div>
                          <div className="text-center p-2 bg-gray-50 rounded"><p className="text-xs font-bold text-red-600">{selectedAgent.recent_flags?.length || 0}</p><p className="text-[10px] text-gray-500">Recent IOCs</p></div>
                          <div className="text-center p-2 bg-gray-50 rounded"><p className="text-xs font-bold text-gray-900">{a.uptime_hours ? `${Math.round(a.uptime_hours)}h` : 'N/A'}</p><p className="text-[10px] text-gray-500">Uptime</p></div>
                          <div className="text-center p-2 bg-gray-50 rounded"><p className="text-xs font-bold text-gray-900">{a.last_heartbeat ? new Date(a.last_heartbeat).toLocaleTimeString() : 'N/A'}</p><p className="text-[10px] text-gray-500">Last Heartbeat</p></div>
                        </div>
                        {selectedAgent.recent_flags?.length > 0 && (
                          <div className="space-y-1">{selectedAgent.recent_flags.slice(0, 5).map(f => (
                            <div key={f.id} className="flex items-center justify-between p-2 bg-red-50 rounded text-xs">
                              <span className="font-mono">{f.process_name}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${SEVERITY_COLORS[f.severity]}`}>{f.severity}</span>
                            </div>
                          ))}</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {agents.agents.length === 0 && <p className="text-center text-gray-400 py-8 bg-white rounded-lg border border-gray-200">No agents registered. Go to Deploy Agent tab to get started.</p>}
              </div>
            </div>
          )}

          {/* Correlation */}
          {view === 'correlate' && correlations && (
            <div className="space-y-4" data-testid="sysmon-correlate">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center"><p className="text-2xl font-bold text-red-600">{correlations.total_flags}</p><p className="text-[10px] text-gray-500">Total Flags (48h)</p></div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center"><p className="text-2xl font-bold text-[#D4A843]">{correlations.unique_hosts}</p><p className="text-[10px] text-gray-500">Unique Hosts</p></div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center"><p className="text-2xl font-bold text-amber-600">{correlations.correlations?.length || 0}</p><p className="text-[10px] text-gray-500">Attack Techniques</p></div>
              </div>
              {correlations.correlations?.map((c, i) => (
                <div key={`item-${i}`} className={`border rounded-lg p-4 ${c.multi_host ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`} data-testid={`corr-${i}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-gray-900 flex items-center gap-2">{c.technique} {c.multi_host && <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded">MULTI-HOST</span>}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">Hosts: {c.hosts.join(', ')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-gray-900">{c.count}x</span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${SEVERITY_COLORS[c.severity]}`}>{c.severity}</span>
                    </div>
                  </div>
                  {c.events?.length > 0 && (
                    <div className="mt-2 space-y-1">{c.events.slice(0, 3).map((e, j) => (
                      <p key={`j-${j}`} className="text-[10px] text-gray-500 font-mono">{e.hostname} — {e.process} — {new Date(e.time).toLocaleString()}</p>
                    ))}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Deployment Dashboard */}
          {view === 'deployment' && deployment && (
            <div className="space-y-4" data-testid="sysmon-deployment">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center hover-lift"><p className="text-2xl font-bold text-gray-900">{deployment.summary.total}</p><p className="text-[10px] text-gray-500">Total Deployed</p></div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 text-center hover-lift"><p className="text-2xl font-bold text-emerald-700">{deployment.summary.healthy}</p><p className="text-[10px] text-emerald-600">Healthy</p></div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 text-center hover-lift"><p className="text-2xl font-bold text-amber-700">{deployment.summary.degraded}</p><p className="text-[10px] text-amber-600">Degraded</p></div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-5 text-center hover-lift"><p className="text-2xl font-bold text-red-700">{deployment.summary.offline}</p><p className="text-[10px] text-red-600">Offline</p></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">By OS</h3>
                  {Object.entries(deployment.summary.by_os || {}).map(([os, count]) => (
                    <div key={os} className="flex items-center justify-between py-1.5"><span className="text-xs text-gray-600 capitalize">{os}</span><span className="text-xs font-bold text-gray-900">{count}</span></div>
                  ))}
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">By Version</h3>
                  {Object.entries(deployment.summary.by_version || {}).map(([ver, count]) => (
                    <div key={ver} className="flex items-center justify-between py-1.5">
                      <span className="text-xs text-gray-600 font-mono">v{ver}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-900">{count}</span>
                        {ver !== deployment.latest_version && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">UPDATE</span>}
                      </div>
                    </div>
                  ))}
                  <p className="text-[10px] text-gray-400 mt-2">Latest: v{deployment.latest_version}</p>
                </div>
              </div>
              {deployment.summary.needs_update?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-amber-800 mb-2">Agents Needing Update ({deployment.summary.needs_update.length})</h3>
                  <div className="space-y-1">{deployment.summary.needs_update.map(a => (
                    <p key={a.agent_id} className="text-xs text-amber-700"><span className="font-mono font-bold">{a.hostname}</span> — v{a.current_version} → v{a.latest_version}</p>
                  ))}</div>
                </div>
              )}
            </div>
          )}

          {/* Threat Briefing */}
          {view === 'briefing' && (
            <div className="space-y-4" data-testid="sysmon-briefing">
              {!briefing && !briefingLoading && (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <Shield className="w-10 h-10 text-[#D4A843] mx-auto mb-3" />
                  <p className="text-sm font-bold text-gray-900 mb-1">AI Threat Intelligence Briefing</p>
                  <p className="text-xs text-gray-400 mb-4">Analyzes the last 24 hours of Sysmon data across all agents</p>
                  <Button onClick={loadBriefing} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="generate-briefing"><Shield className="w-3.5 h-3.5 mr-1" /> Generate Briefing</Button>
                </div>
              )}
              {briefingLoading && <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#D4A843]" /><span className="ml-2 text-sm text-gray-500">Analyzing threat data...</span></div>}
              {briefing && (
                <div className="animate-fade-in">
                  <div className={`rounded-xl p-6 mb-4 ${briefing.threat_level === 'CRITICAL' ? 'bg-red-600' : briefing.threat_level === 'HIGH' ? 'bg-orange-600' : briefing.threat_level === 'MEDIUM' ? 'bg-amber-500' : 'bg-emerald-600'} text-white`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider opacity-80">Threat Level</p>
                        <p className="text-3xl font-bold">{briefing.threat_level || 'N/A'}</p>
                      </div>
                      {briefing.metrics && (
                        <div className="flex gap-6 text-right">
                          <div><p className="text-2xl font-bold">{briefing.metrics.events_analyzed?.toLocaleString()}</p><p className="text-[10px] opacity-80">Events</p></div>
                          <div><p className="text-2xl font-bold">{briefing.metrics.iocs_detected}</p><p className="text-[10px] opacity-80">IOCs</p></div>
                          <div><p className="text-2xl font-bold">{briefing.metrics.hosts_monitored}</p><p className="text-[10px] opacity-80">Hosts</p></div>
                          <div><p className="text-2xl font-bold">{briefing.metrics.risk_score}</p><p className="text-[10px] opacity-80">Risk Score</p></div>
                        </div>
                      )}
                    </div>
                    {briefing.executive_summary && <p className="mt-3 text-sm opacity-90">{briefing.executive_summary}</p>}
                  </div>
                  {briefing.key_findings?.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Key Findings</h3>
                      <ul className="space-y-2">{briefing.key_findings.map((f, i) => <li key={`item-${i}`} className="text-sm text-gray-700 flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />{f}</li>)}</ul>
                    </div>
                  )}
                  {briefing.active_threats?.length > 0 && (
                    <div className="space-y-3 mb-4">{briefing.active_threats.map((t, i) => (
                      <div key={`item-${i}`} className={`border rounded-lg p-4 ${t.severity === 'critical' ? 'bg-red-50 border-red-200' : t.severity === 'high' ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-bold text-gray-900">{t.name}</p>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${SEVERITY_COLORS[t.severity]}`}>{t.severity}</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{t.description}</p>
                        {t.affected_hosts?.length > 0 && <p className="text-[10px] text-gray-400 mt-1">Hosts: {t.affected_hosts.join(', ')}</p>}
                        <p className="text-xs text-[#D4A843] mt-2 font-medium">{t.recommendation}</p>
                      </div>
                    ))}</div>
                  )}
                  {briefing.recommendations?.length > 0 && (
                    <div className="bg-[#D4A843]/5 border border-[#D4A843]/20 rounded-lg p-5">
                      <h3 className="text-xs font-bold text-[#D4A843] uppercase tracking-wider mb-3">Recommendations</h3>
                      <ol className="space-y-1.5 list-decimal list-inside">{briefing.recommendations.map((r, i) => <li key={`item-${i}`} className="text-sm text-gray-700">{r}</li>)}</ol>
                    </div>
                  )}
                  <div className="flex justify-end mt-4">
                    <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={loadBriefing} data-testid="refresh-briefing"><RefreshCw className="w-3 h-3 mr-1" /> Regenerate</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Deploy Agent */}
          {view === 'deploy' && (
            <div className="space-y-4" data-testid="sysmon-deploy">
              <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Download className="w-4 h-4 text-[#D4A843]" /> Deploy Sysmon Agent</h2>
              <p className="text-xs text-gray-500">Generate an install script for your endpoints. The agent will auto-register, send heartbeats, and push Sysmon events.</p>
              <div className="flex gap-3">
                <Button size="sm" onClick={() => loadInstallScript('windows')} className={`rounded-lg text-xs ${installScript?.os_type === 'windows' ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-white text-gray-700 border border-gray-200'}`} data-testid="deploy-windows"><Terminal className="w-3.5 h-3.5 mr-1" /> Windows PowerShell</Button>
                <Button size="sm" onClick={() => loadInstallScript('linux')} className={`rounded-lg text-xs ${installScript?.os_type === 'linux' ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-white text-gray-700 border border-gray-200'}`} data-testid="deploy-linux"><Terminal className="w-3.5 h-3.5 mr-1" /> Linux Bash</Button>
              </div>
              {installScript && (
                <div className="bg-gray-900 rounded-lg p-5 relative animate-fade-in" data-testid="deploy-script">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-400 font-mono">{installScript.os_type === 'windows' ? 'PowerShell' : 'Bash'} Install Script</span>
                    <Button size="sm" variant="ghost" className="text-xs text-gray-400 hover:text-white" onClick={copyScript} data-testid="deploy-copy"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                  </div>
                  <pre className="text-xs text-emerald-400 font-mono whitespace-pre-wrap overflow-x-auto max-h-96">{installScript.script}</pre>
                </div>
              )}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-xs font-bold text-blue-800 mb-2">How it works</h3>
                <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Copy the script and run it on the target machine</li>
                  <li>The agent auto-registers with this platform and receives an API key</li>
                  <li>Heartbeats are sent every 60 seconds with system stats</li>
                  <li>Sysmon/audit events are collected and pushed in batches</li>
                  <li>IOC detection runs automatically on ingested events</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
